import { useCallback } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import { pagesText } from 'src/constants/pagesText';
import ApplicationsService from 'src/services/applications.service';
import parseError from 'src/utils/parseError';
import { handleUrlDownload } from 'src/utils/download.util';
import { getResourceTypeIcon, getResourceTypeLabel } from 'src/utils/enums/resourceType';

// The generalized artifacts an import shipped alongside a file set. They are downloads rather than
// anything we deploy, so they are links rather than a table of their own.
const FileSetTemplateLinks = ({ fileSet, emptyLabel = '—' }) => {
  const { enqueueSnackbar } = useSnackbar();

  const onDownload = useCallback(
    async template => {
      try {
        const url = await ApplicationsService.getSecurityDownloadUrl({
          resourceId: fileSet.applicationId,
          key: template.s3Key,
        });
        handleUrlDownload(url, template.fileName);
      } catch (error) {
        enqueueSnackbar(parseError(error) || pagesText.somethingWentWrong, {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    },
    [enqueueSnackbar, fileSet],
  );

  if (!fileSet?.templates?.length) {
    return (
      <Typography component="span" sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
      {fileSet.templates.map(template => (
        <Tooltip
          key={template.s3Key}
          title={template.fileName}
          arrow
          componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320 } } }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.4}
            onClick={event => {
              event.stopPropagation();
              onDownload(template);
            }}
            sx={{ cursor: 'pointer', color: '#87C8E0' }}>
            <Iconify icon={getResourceTypeIcon(template.resourceType)} width={14} height={14} />
            <Box component="span" sx={{ fontSize: '0.7rem' }}>
              {getResourceTypeLabel(template.resourceType)}
            </Box>
          </Stack>
        </Tooltip>
      ))}
    </Stack>
  );
};

export default FileSetTemplateLinks;
